import { FC } from 'react';
import { EMPTY_DOCUMENT } from '@contentful/rich-text-types';
import { buttonClick } from '@belong/analytics';
import { useAnalyticsEvents } from '@belong/providers/analyticsEvents';
import { FONT_COLOURS } from '@belong/themes';
import { IMAGE_ASPECT_RATIO, IPrimaryBanner, TEXT_NODES } from '@belong/types';
import { isExternalUrl } from '@belong/url-utils';
import { Button } from '../../actions/Button';
import Callout from '../../feedback/Callout';
import ContentFormatter from '../../styles/ContentFormatter';
import { Heading } from '../../styles/Typography/Heading';
import { NODES as defaultRenderers, RichText } from '../../styles/Typography/RichText/RichText';
import { IRoamingCountriesProps, RoamingCountries } from '../RoamingCountries';
import { FRoamingCountries } from './countryList.factory';
import * as Banner from './PrimaryBanner.styles';

export const PrimaryBanner: FC<IPrimaryBanner> = ({
  id,
  identifier,
  link,
  pageHeading,
  bannerHeading,
  content = EMPTY_DOCUMENT,
  finePrint,
  hideImageOnSmall = false,
  image,
  callout,
  video,
  leftAlignBanner = false,
  layout,
  variation = 'large',
  aspectRatio = IMAGE_ASPECT_RATIO.letterbox
}) => {
  let media;
  if (video) {
    media = (
      <Banner.VideoWrapper variation={variation} hideImageOnSmall={hideImageOnSmall} ratio={aspectRatio}>
        <Banner.StyledVideoContainer {...video} />
      </Banner.VideoWrapper>
    );
  } else {
    media = (
      <Banner.ImageWrapper variation={variation} hideImageOnSmall={hideImageOnSmall} ratio={aspectRatio}>
        <Banner.Image
          leftAlign={leftAlignBanner}
          loading="eager"
          src={{
            xs: hideImageOnSmall ? [] : [image.src, { w: 360, h: 260, fit: 'fill', f: 'center' }], // For small screens, hide the image if `hideImageOnSmall` is true
            sm: hideImageOnSmall ? [] : [image.src, { w: 800, h: 600, fit: 'fill', f: 'center' }],
            md: [image.src, { w: 520, h: 390, fit: 'fill', f: 'center' }],
            lg: [image.src, { w: 720, h: 510, fit: 'fill', f: 'center' }],
            xl: [image.src, { w: 1100, h: 510, fit: 'fill', f: 'center' }]
          }}
          alt={image.alt}
          contentType={image.contentType}
          fetchPriority="high"
        />
      </Banner.ImageWrapper>
    );
  }

  const analyticsEvents = useAnalyticsEvents();
  const onClickHandler = () => {
    analyticsEvents.send({
      ...buttonClick({
        label: (link?.label || 'primary-banner-link').toLowerCase().replace(/\s/g, '-'),
        text: link?.label,
        destination: isExternalUrl(link?.href) ? 'external' : 'internal'
      }),
      component: 'primary-banner'
    });
  };

  const hasHeading = !!pageHeading;
  const pageHeadingElement = hasHeading && (
    <Banner.HeadingWrapper>
      <Heading as="h1" variant="small" hasColor={FONT_COLOURS.LIGHT}>
        {pageHeading}
      </Heading>
    </Banner.HeadingWrapper>
  );

  return (
    <div>
      {hasHeading && <Banner.MobileOnly>{pageHeadingElement}</Banner.MobileOnly>}
      <Banner.OuterWrapper
        data-contentid={id}
        data-identifier={identifier}
        data-testid="section-primary-banner"
        layout={layout}
      >
        <Banner.ContentWrapper layout={layout}>
          {hasHeading && <Banner.DesktopOnly>{pageHeadingElement}</Banner.DesktopOnly>}
          {callout && (
            <Banner.DesktopOnly>
              <Banner.CalloutWrapper withTopSpace={hasHeading}>
                <Callout {...callout} data-testid="primary-banner-callout-desktop" />
              </Banner.CalloutWrapper>
            </Banner.DesktopOnly>
          )}
          <ContentFormatter spacing="large">
            {bannerHeading && (
              <Heading as={hasHeading ? 'h2' : 'h1'} variant="xxlarge" hasColor={FONT_COLOURS.LIGHT}>
                {bannerHeading}
              </Heading>
            )}
            <RichText
              html={content}
              p={TEXT_NODES.copyLarge}
              hasColor={FONT_COLOURS.LIGHT}
              renderBlockEntries={{
                countryList: entryProps => {
                  const props: IRoamingCountriesProps = FRoamingCountries(entryProps?.data?.target || {});
                  return <RoamingCountries {...props} as="div" />;
                }
              }}
              renderEmbeddedInlines={{
                elementEmbeddedInformationModal: entryProps => {
                  return defaultRenderers.elementEmbeddedInformationModal(entryProps, {
                    isLightColor: true,
                    isSmallFontSize: true
                  });
                }
              }}
            />
            {finePrint && (
              <RichText
                html={finePrint}
                p={TEXT_NODES.copySmall}
                hasColor={FONT_COLOURS.LIGHT}
                renderEmbeddedInlines={{
                  elementEmbeddedInformationModal: entryProps => {
                    const modal = defaultRenderers.elementEmbeddedInformationModal(entryProps, {
                      isLightColor: true,
                      isSmallFontSize: true
                    });
                    return <Banner.FinePrintWrapper>{modal}</Banner.FinePrintWrapper>;
                  }
                }}
              />
            )}
          </ContentFormatter>
          {link && (
            <Banner.ButtonWrapper>
              <Button isLightColor {...link} onClick={onClickHandler} width={{ xs: 'full', md: 'default' }}>
                {link.children || link.label}
              </Button>
            </Banner.ButtonWrapper>
          )}
        </Banner.ContentWrapper>
        {media}
        {callout && (
          <Banner.MobileOnly>
            <Banner.ContentWrapper layout={layout} style={{ paddingBottom: 0 }}>
              <Banner.CalloutWrapper>
                <Callout {...callout} data-testid="primary-banner-callout-mobile" />
              </Banner.CalloutWrapper>
            </Banner.ContentWrapper>
          </Banner.MobileOnly>
        )}
      </Banner.OuterWrapper>
    </div>
  );
};

PrimaryBanner.displayName = 'PrimaryBanner';

export default PrimaryBanner;
